import React, { useCallback, useEffect, useState } from 'react';
import { createSearchParams, NavigateFunction, Location, SetURLSearchParams } from 'react-router-dom';
import { deleteSchedule } from '../../../api/schedule';
import { ScheduleInfo } from '../../../types/schedule.types';
import type { ScheduleDataCache } from '../../../types/cache.types';
import { useScheduleDataLoader } from '../../../hooks/useScheduleDataLoader';
import {
  type ScheduleDetailsTabType,
  parseScheduleDetailsTabId,
} from '../Components/TabPages/tabPages.types';

export type { ScheduleDetailsTabType };

/** 切换/进入拍摄计划时传给 navigate 的附加选项（与 handleSelectSchedule 一致） */
export type SelectScheduleNavigateOptions = {
  fromScheduleList?: boolean;
  /** 新建拍摄计划后默认打开右侧「场次列表」标签页 */
  openScenesListTab?: boolean;
  /** 切换拍摄计划后右侧停留在「计划管理」标签页（如复制拍摄计划并打开新版本） */
  openDetailsTab?: boolean;
};

function wantsOpenDetailsTab(state: unknown): boolean {
  return (
    typeof state === 'object' &&
    state !== null &&
    'openDetailsTab' in state &&
    Boolean((state as { openDetailsTab?: boolean }).openDetailsTab)
  );
}

function wantsOpenScenesListTab(state: unknown): boolean {
  return (
    typeof state === 'object' &&
    state !== null &&
    'openScenesListTab' in state &&
    Boolean((state as { openScenesListTab?: boolean }).openScenesListTab)
  );
}

export interface ScheduleLayoutTab {
  id: string;
  title: string;
  active: boolean;
  fixed?: boolean;
  path?: string;
  previousTabId?: string;
}

export interface UseScheduleLifecycleParams {
  scheduleID: string | undefined;
  preloadedScheduleProp: ScheduleInfo | null | undefined;
  activeTab: string;
  breakdownId: string | null | undefined;
  navigate: NavigateFunction;
  location: Location;
  searchParams: URLSearchParams;
  setSearchParams: SetURLSearchParams;
  localOnlyData: { userLastUsedScheduleTab?: string[] | null };
  serverSyncedData: { userLastUsedScheduleID?: string[] | null };
  tabs: ScheduleLayoutTab[];
  setTabs: React.Dispatch<React.SetStateAction<ScheduleLayoutTab[]>>;
  scheduleDetailRef: React.MutableRefObject<ScheduleInfo | null>;
  getSlotCache: (scheduleId: string) => { scheduleInfo?: ScheduleInfo | null } | null;
  getScheduleCache: (breakdownID: string) => ScheduleDataCache | null | undefined;
  setScheduleCache: (breakdownID: string, data: Partial<ScheduleDataCache>) => void;
  clearSlotCache: (scheduleId: string) => void;
  clearCalDateCache: (scheduleId: string) => void;
  setServerSyncedField: (key: string, value: string) => void;
  setLocalOnlyField: (field: 'userLastUsedScheduleTab', value: string) => void;
  stripServerSyncedUserLastUsedScheduleByScheduleId: (scheduleId: string) => void;
  /** 由页面在每轮渲染中赋值，供删除拍摄计划后刷新 */
  refreshScheduleCalendarDataRef: React.MutableRefObject<() => Promise<void>>;
}

export function useScheduleLifecycle(p: UseScheduleLifecycleParams) {
  const {
    scheduleID,
    preloadedScheduleProp,
    activeTab,
    breakdownId,
    navigate,
    location,
    searchParams,
    setSearchParams,
    localOnlyData,
    serverSyncedData,
    tabs,
    setTabs,
    scheduleDetailRef,
    getSlotCache,
    getScheduleCache,
    setScheduleCache,
    clearSlotCache,
    clearCalDateCache,
    setServerSyncedField,
    setLocalOnlyField,
    stripServerSyncedUserLastUsedScheduleByScheduleId,
    refreshScheduleCalendarDataRef,
  } = p;

  const [currentScheduleId, setCurrentScheduleId] = useState<string | undefined>(scheduleID);
  const [shouldForceRefresh, setShouldForceRefresh] = useState(false);

  const [scheduleDetailsActiveTab, setScheduleDetailsActiveTab] = useState<ScheduleDetailsTabType>(() => {
    if (activeTab !== 'schedule') return 'currentDate';
    if (!scheduleID) {
      const urlTabNoSchedule = parseScheduleDetailsTabId(searchParams.get('tab'));
      if (urlTabNoSchedule) return urlTabNoSchedule;
      const st = location.state;
      if (
        typeof st === 'object' &&
        st !== null &&
        (st as { noScheduleSelected?: boolean }).noScheduleSelected
      ) {
        return 'details';
      }
      return 'currentDate';
    }
    if (location.state?.fromScheduleList || wantsOpenDetailsTab(location.state)) return 'details';
    if (wantsOpenScenesListTab(location.state)) return 'scenes';
    const urlTab = parseScheduleDetailsTabId(searchParams.get('tab'));
    if (urlTab) return urlTab;
    const scheduleTabs = localOnlyData.userLastUsedScheduleTab;
    if (Array.isArray(scheduleTabs) && scheduleTabs.length > 0) {
      const tabCache = scheduleTabs.find((item) => {
        const parts = item.split(';');
        return parts.length >= 2 && parts[0] === scheduleID;
      });
      if (tabCache) {
        const tabName = tabCache.split(';')[1];
        const parsed = parseScheduleDetailsTabId(tabName);
        if (parsed) return parsed;
      }
    }
    return 'currentDate';
  });

  useEffect(() => {
    if (scheduleID && scheduleID !== currentScheduleId) {
      setCurrentScheduleId(scheduleID);
      setShouldForceRefresh(true);
    }
  }, [scheduleID, currentScheduleId]);

  useEffect(() => {
    if (location.state?.noScheduleSelected) return;
    if (activeTab === 'schedule' && !scheduleID && breakdownId && serverSyncedData.userLastUsedScheduleID) {
      const scheduleIDs = serverSyncedData.userLastUsedScheduleID;
      if (Array.isArray(scheduleIDs) && scheduleIDs.length > 0) {
        const lastUsedScheduleEntry = scheduleIDs.find((item) => {
          const parts = item.split(';');
          return parts.length >= 2 && parts[0] === breakdownId;
        });
        if (lastUsedScheduleEntry) {
          const parts = lastUsedScheduleEntry.split(';');
          const cachedScheduleId = parts[1]?.trim();
          if (!cachedScheduleId) return;
          const slice = getScheduleCache(breakdownId);
          const list = slice?.schedules;
          if (Array.isArray(list) && list.length > 0 && !list.some((p) => p.scheduleID === cachedScheduleId)) {
            navigate(
              { pathname: '/schedule', search: createSearchParams({ tab: 'details' }).toString() },
              { replace: true, state: { noScheduleSelected: true, breakdownID: breakdownId } },
            );
            return;
          }
          navigate(`/schedule/${cachedScheduleId}`, { replace: true });
        }
      }
    }
  }, [activeTab, scheduleID, breakdownId, serverSyncedData.userLastUsedScheduleID, navigate, location.state?.noScheduleSelected, getScheduleCache]);

  useEffect(() => {
    if (activeTab !== 'schedule' || !scheduleID) return;
    if (location.state?.fromScheduleList || wantsOpenDetailsTab(location.state)) {
      setScheduleDetailsActiveTab('details');
      const nextSearch = new URLSearchParams(searchParams);
      nextSearch.set('tab', 'details');
      navigate({ pathname: location.pathname, search: nextSearch.toString() }, { replace: true, state: undefined });
      return;
    }
    if (wantsOpenScenesListTab(location.state)) {
      setScheduleDetailsActiveTab('scenes');
      setLocalOnlyField('userLastUsedScheduleTab', `${scheduleID};scenes`);
      const nextSearch = new URLSearchParams(searchParams);
      nextSearch.set('tab', 'scenes');
      navigate({ pathname: location.pathname, search: nextSearch.toString() }, { replace: true, state: undefined });
      return;
    }
    const urlTab = parseScheduleDetailsTabId(searchParams.get('tab'));
    if (urlTab) {
      setScheduleDetailsActiveTab(urlTab);
      return;
    }
    if (localOnlyData.userLastUsedScheduleTab) {
      const scheduleTabs = localOnlyData.userLastUsedScheduleTab;
      if (Array.isArray(scheduleTabs) && scheduleTabs.length > 0) {
        const tabCache = scheduleTabs.find((item) => {
          const parts = item.split(';');
          return parts.length >= 2 && parts[0] === scheduleID;
        });
        if (tabCache) {
          const parts = tabCache.split(';');
          const parsed = parseScheduleDetailsTabId(parts[1]);
          if (parsed) {
            setScheduleDetailsActiveTab(parsed);
            setSearchParams(
              (prev) => {
                const next = new URLSearchParams(prev);
                next.set('tab', parsed);
                return next;
              },
              { replace: true },
            );
          }
        }
      }
    }
  }, [
    activeTab,
    scheduleID,
    localOnlyData.userLastUsedScheduleTab,
    location.state?.fromScheduleList,
    location.state,
    location.pathname,
    searchParams,
    setSearchParams,
    navigate,
    setLocalOnlyField,
  ]);

  const {
    schedule: refreshedSchedule,
    loading: refreshLoading,
  } = useScheduleDataLoader({
    scheduleId: currentScheduleId || '',
    forceRefresh: shouldForceRefresh || (!!currentScheduleId && !preloadedScheduleProp),
  });

  useEffect(() => {
    if (shouldForceRefresh && !refreshLoading && refreshedSchedule) {
      setShouldForceRefresh(false);
    }
  }, [shouldForceRefresh, refreshLoading, refreshedSchedule]);

  const handleDetailsTabChange = useCallback(
    (tab: ScheduleDetailsTabType) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('tab', tab);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const handleSelectSchedule = async (selectedScheduleId: string, options?: SelectScheduleNavigateOptions) => {
    if (selectedScheduleId !== currentScheduleId) {
      setCurrentScheduleId(selectedScheduleId);
      setShouldForceRefresh(true);
    }

    const resolvedBreakdownIdForLastUsed =
      breakdownId ||
      scheduleDetailRef.current?.scheduleBelongBreakdownID ||
      (location.state && typeof location.state === 'object' && 'breakdownID' in location.state
        ? (location.state as { breakdownID?: string }).breakdownID
        : undefined);
    if (resolvedBreakdownIdForLastUsed && selectedScheduleId) {
      // 关键：进入/切换拍摄计划时同时绑定当前分场，避免刷新后 breakdownId 与 scheduleId 脱钩
      setServerSyncedField('userLastUsedBreakdownID', resolvedBreakdownIdForLastUsed);
      setServerSyncedField('userLastUsedScheduleID', `${resolvedBreakdownIdForLastUsed};${selectedScheduleId}`);
    }

    const getScheduleName = () => {
      const slotCache = getSlotCache(selectedScheduleId);
      const scheduleSnapshot = slotCache?.scheduleInfo;
      if (scheduleSnapshot) {
        return scheduleSnapshot.scheduleName || '未命名';
      }
      if (refreshedSchedule && refreshedSchedule.scheduleID === selectedScheduleId) {
        return refreshedSchedule.scheduleName || '未命名';
      }
      return '未命名';
    };

    const scheduleName = getScheduleName();
    const tabId = `schedule-${selectedScheduleId}`;

    const existingTab = tabs.find((tab) => tab.id === tabId);

    if (existingTab) {
      setTabs((prev) =>
        prev.map((tab) => ({
          ...tab,
          active: tab.id === tabId,
          title: tab.id === tabId ? scheduleName : tab.title,
        })),
      );
    } else {
      const currentActiveTab = tabs.find((tab) => tab.active);
      setTabs((prev) => [
        ...prev.map((tab) => ({
          ...tab,
          active: false,
        })),
        {
          id: tabId,
          title: scheduleName,
          active: true,
          path: `/schedule/${selectedScheduleId}`,
          previousTabId: currentActiveTab?.id,
        },
      ]);
    }

    const { fromScheduleList, openScenesListTab, openDetailsTab } = options ?? {};
    const navState: SelectScheduleNavigateOptions | undefined =
      fromScheduleList || openScenesListTab || openDetailsTab
        ? {
            ...(fromScheduleList ? { fromScheduleList: true as const } : {}),
            ...(openScenesListTab ? { openScenesListTab: true as const } : {}),
            ...(openDetailsTab ? { openDetailsTab: true as const } : {}),
          }
        : undefined;
    navigate(`/schedule/${selectedScheduleId}`, { state: navState });
  };

  const handleConfirmDeleteSchedule = useCallback(
    async (scheduleIdToDelete: string) => {
      const breakdownIDForNav = scheduleDetailRef.current?.scheduleBelongBreakdownID ?? location.state?.breakdownID;
      await deleteSchedule(scheduleIdToDelete);
      stripServerSyncedUserLastUsedScheduleByScheduleId(scheduleIdToDelete);
      if (breakdownIDForNav) {
        const cache = getScheduleCache(breakdownIDForNav);
        if (cache) {
          const nextSchedules = (cache.schedules || []).filter((p) => p.scheduleID !== scheduleIdToDelete);
          const clearCurrent = scheduleIdToDelete === cache.currentScheduleId;
          setScheduleCache(breakdownIDForNav, {
            schedules: nextSchedules,
            ...(clearCurrent
              ? {
                  currentScheduleId: null,
                  currentScheduleInfo: null,
                  currentSlots: [],
                  currentCalDates: [],
                }
              : {}),
          });
        }
        clearSlotCache(scheduleIdToDelete);
        clearCalDateCache(scheduleIdToDelete);
      }
      await refreshScheduleCalendarDataRef.current();
      if (scheduleIdToDelete === scheduleID) {
        setCurrentScheduleId(undefined);
        navigate(
          { pathname: '/schedule', search: createSearchParams({ tab: 'details' }).toString() },
          { state: { noScheduleSelected: true, breakdownID: breakdownIDForNav } },
        );
      }
    },
    [
      scheduleID,
      navigate,
      location.state?.breakdownID,
      getScheduleCache,
      setScheduleCache,
      clearSlotCache,
      clearCalDateCache,
      refreshScheduleCalendarDataRef,
      scheduleDetailRef,
      stripServerSyncedUserLastUsedScheduleByScheduleId,
    ],
  );

  return {
    currentScheduleId,
    refreshedSchedule,
    refreshLoading,
    scheduleDetailsActiveTab,
    setScheduleDetailsActiveTab,
    handleDetailsTabChange,
    handleSelectSchedule,
    handleConfirmDeleteSchedule,
  };
}
